/**
 * ErrorBoundary global.
 * Estructura DOBLE: uno fuera y otro dentro del Suspense.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

type Props = { children: ReactNode; fallback?: (err: Error, reset: () => void) => ReactNode };
type State = { hasError: boolean; error: Error | null };

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info);
  }

  reset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError && this.state.error) {
      return this.props.fallback ? this.props.fallback(this.state.error, this.reset) : <DefaultErrorFallback error={this.state.error} reset={this.reset} />;
    }
    return this.props.children;
  }
}

function DefaultErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-md">
        <span className="material-symbols-outlined text-5xl text-error">error</span>
        <h1 className="mt-3 text-lg font-semibold text-slate-900">Algo salió mal</h1>
        <p className="mt-1 text-sm text-slate-600">{error.message}</p>
        <details className="mt-3">
          <summary className="cursor-pointer text-xs text-slate-500">Ver stack</summary>
          <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-100 p-2 text-[10px] text-slate-700">
            {error.stack}
          </pre>
        </details>
        <button
          type="button"
          onClick={reset}
          className="mt-4 inline-flex items-center gap-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-hover"
        >
          <span className="material-symbols-outlined text-[18px]">refresh</span>
          Reintentar
        </button>
      </div>
    </div>
  );
}
